import React, { useEffect, useState, useMemo, useCallback } from "react";
import { Form, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useForm, FormProvider } from "react-hook-form";
import PriceSearchFilter from "../components/PriceSearchFilter";

import useGetFilterLookup from "../hooks/useGetFilterLookup";

import { clearCache, getTrilhaCliente } from "~services/GlobalServices";
import PriceSearchDisclaimer from "~pages/PriceSearchV2/components/PriceSearchDisclaimer";
import ClientHeader from "~components/ClientInfo/ClientHeader";
import PriceSearchCards from "../components/PriceSearchCards";
import usePriceSearchData from "../hooks/usePriceSearchData";
import useCliente from "~fetchs/useCliente";
import useCheckIn from "~fetchs/Roterizacao/useCheckIn";
import usePesquisaGrupoMaterial from "~fetchs/PriceSearch/usePesquisaGrupoMaterial";
import usePesquisaProdutos from "~fetchs/PriceSearch/usePesquisaProdutos";
import usePesquisaCanalDistribuicao from "~fetchs/PriceSearch/usePesquisaCanalDistribuicao";
import usePesquisaEmbalagem from "~fetchs/PriceSearch/usePesquisaEmbalagem";
import usePesquisaMedida from "~fetchs/PriceSearch/usePesquisaMedida";
import usePesquisaPlayer from "~fetchs/PriceSearch/usePesquisaPlayer";
import usePesquisaPrazo from "~fetchs/PriceSearch/usePesquisaPrazo";
import usePesquisaMotivoCompra from "~fetchs/PriceSearch/usePesquisaMotivoCompra";
import usePesquisaCentroOrigem from "~fetchs/PriceSearch/usePesquisaCentroOrigem";
import useParametros from "~fetchs/useParametros";
import ModalAction from "~components/Modal/ModalAction";
import PriceSearchCharts from "../components/PriceSearchCharts";
import { db } from "~db/db";
import usePriceSearch from "../hooks/usePriceSearch";
import { Spinner } from "@chakra-ui/react";
import WarningBar from "../components/WarningBar";
import useOnlineStatus from "~hooks/useOnlineStatus";
import useButtonsConfig from "../hooks/useButtonsConfig";
import MessageForm from "~components/Form/components/MessageForm";
import ModalResponse from "~components/Modal/ModalResponse";
import useModalAction from "~components/Modal/hooks/useModalAction";
import useModalResponse from "~components/Modal/hooks/useModalResponse";
import ModalForUpdate from "~components/Modal/ModalForUpdate";
import PriceSearchUpdateModal from "~pages/PriceSearchV2/components/PriceSearchUpdateModal";
import PriceSearchClientShareModal from "../components/PriceSearchClientShareModal";
import useClientShare from "../hooks/useClientShare";
import PriceSearchRequiredGroups from "../components/PriceSearchRequiredGroups";
import SearchNotUpdatedModal from "../components/SearchNotUpdatedModal";
import useStore from "../hooks/useStore";
import { useConfigureFooter } from "~hooks/useHelpers";
import { getLocation } from "~services/utils/geoLocation";
import { deepCopy } from "~services/utils/deepCopy";
import { deviceType, isDesktop, isTablet } from "react-device-detect";
import { formatDateToCustomString } from "../utils/searchHelpers";
import { WarningIcon2 } from "~misc/icons";
import useRequiredGroups from "../hooks/useRequiredGroups";
import useUnsavedChangesWarning from "~components/Form/hooks/useUnsavedChangesWarning";
import LoaderFull from "~components/Form/components/LoaderFull";

const PriceSearch = () => {
  const { parameter: idCliente } = useParams();
  const [searchParams] = useSearchParams();
  const origem = searchParams.get("origem");
  const deletar = searchParams.get("deletar");
  const cameFromVisitaView = searchParams.get("cameFromVisitaView");
  const isOnline = useOnlineStatus();
  const navigate = useNavigate();
  const [isLoadingLocation, setIsLoadingLocation] = useState(false);

  const { cliente } = useCliente(idCliente);
  const { checkin } = useCheckIn();

  const pesquisaGrupoMateriais = usePesquisaGrupoMaterial();
  const pesquisaProdutos = usePesquisaProdutos();
  const pesquisaCanaisDistribuicao = usePesquisaCanalDistribuicao();
  const pesquisaEmbalagens = usePesquisaEmbalagem({});
  const pesquisaMedidas = usePesquisaMedida();
  const pesquisaPlayers = usePesquisaPlayer({});
  const pesquisaPrazos = usePesquisaPrazo();
  const pesquisaCentros = usePesquisaCentroOrigem({});
  const pesquisaMotivosCompra = usePesquisaMotivoCompra({});
  const pesquisaObjetos = useMemo(
    () => ({
      pesquisaGrupoMateriais,
      pesquisaProdutos,
      pesquisaCanaisDistribuicao,
      pesquisaEmbalagens,
      pesquisaMedidas,
      pesquisaPlayers,
      pesquisaPrazos,
      pesquisaCentros,
      pesquisaMotivosCompra,
    }),
    [
      pesquisaGrupoMateriais,
      pesquisaProdutos,
      pesquisaCanaisDistribuicao,
      pesquisaEmbalagens,
      pesquisaMedidas,
      pesquisaPlayers,
      pesquisaPrazos,
      pesquisaCentros,
      pesquisaMotivosCompra,
    ]
  );

  const clienteId = Number.parseInt(idCliente);

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const [selectedSearchId, setSelectedSearchId] = useState(0);
  const { setDeletarId, deletarId } = useStore();

  const toggleDeleteModal = (id) => {
    setSelectedSearchId(id || 0);
    setIsDeleteModalOpen((prev) => !prev);
  };

  async function deleteConfirmed() {
    setDeletarId(selectedSearchId);
    setIsFormDirty(true);
    toggleDeleteModal();
  }

  const methods = useForm({
    defaultValues: {
      grupoProduto: "",
      produto: "",
      periodo: null,
      order: "recentes",
    },
    mode: "onChange",
  });

  const { watch, reset } = methods;
  const filters = watch();

  const {
    filteredData,
    totalItems,
    filteredItemsCount,
    filteredOnce,
    loading,
    updateHook,
    fechadoConsomeBasico,
    dataIsNotPresentable,
    refetchMapaMina,
    showMessageForm,
    setShowMessageForm,
    searchedCount,
    isLoadingData,
    loadingMapaRequest: loadingMapaData,
    lastSearchDate,
    open,
  } = usePriceSearchData({
    clienteId,
    setDeletarId,
    deletarId,
    origem,
    filters: filters,
    pesquisaObjetos,
  });

  const { data: dataLookupFilters, loading: loadingFilters } = useGetFilterLookup({
    loadingMapaData,
    totalItems,
    clienteId,
  });

  const [trilhaCliente, setTrilhaCliente] = useState("");

  useEffect(() => {
    if (cliente?.trilha) setTrilhaCliente(getTrilhaCliente(cliente.trilha?.codigo));
  }, [cliente]);

  const quantidadeDiasMostrarData = 5;
  const [diasMostrarDataAtualizacao, setDiasMostrarDataAtualizacao] = useState(quantidadeDiasMostrarData);
  const { toObjectSlug, getSlug, parameterSlugs } = useParametros();

  useEffect(() => {
    if (toObjectSlug) {
      setDiasMostrarDataAtualizacao(
        getSlug(parameterSlugs.mapaMina.quantidadeDiasMostrarData, quantidadeDiasMostrarData)
      );
    }
  }, [toObjectSlug]);

  const {
    isLoadingPost,
    handleSavePriceSearch,
    setUltimosDadosColetados,
    origens,
    showMessageForm: showMessageFormPost,
    setShowMessageForm: setShowMessageFormPost,
  } = usePriceSearch({
    idCliente: Number.parseInt(idCliente),
    pesquisaObjetos,
    origem,
    cameFromVisitaView,
  });
  const [availableCardsCount, setAvailableCardsCount] = useState([]);
  const [buttonClickClear, setButtonClickClear] = useState(null);
  const [isClearing, setIsClearing] = useState(false);
  const [componentKey, setComponentKey] = useState(0);
  const { onConfirmModal, isModalUpdate, openModalUpdate, closedModalUpdate, dadosColetados, setDadosColetados } =
    PriceSearchUpdateModal();

  useEffect(() => {
    (async () => {
      const count = await db.mapaDaMina
        .where("clienteId")
        .equals(Number.parseInt(idCliente))
        .and((e) => e.ativo)
        .count();
      setAvailableCardsCount(count);
    })();
  }, [filteredData, idCliente]);

  useEffect(() => {
    const checkUpdates = async () => {
      const currentUpdateStatus = await checkforUpdates();
      if (currentUpdateStatus !== previousUpdateStatus) {
        setIsFormDirty(true);
        setPreviousUpdateStatus(currentUpdateStatus);
      }
    };
    checkUpdates();
  }, [idCliente]);

  const getDispositivo = useCallback((isTablet, isDesktop, deviceType) => {
    if (isTablet) return "Tablet";
    if (isDesktop) return "Desktop";
    return deviceType;
  }, []);

  const [wasActionCalled, setWasActionCalled] = useState(false);

  const processPriceSearchData = useCallback(
    async (dadosColetados) => {
      closedModalUpdate();
      const data = [];
      setDadosColetados({});
      const dispositivo = getDispositivo(isTablet, isDesktop, deviceType);

      await db.mapaDaMina
        .where("clienteId")
        .equals(Number.parseInt(idCliente))
        .each(async (e) => data.push(e));

      setIsLoadingLocation(true);
      let pos = await getLocation();

      Object.keys(pos).forEach((key) => {
        pos[key] = pos[key] ? pos[key].toString() : null;
      });
      const toSavePriceSearch = [];

      data.forEach((e) => {
        if (e.ativo || e.updated || e.deletedAt || e.new) {
          const obj = {
            ...deepCopy(e),
            id: 0,
            deletedAt: e.deletedAt ?? e.deletadoEm ?? null,
            ...dadosColetados,
            ...pos,
            volume: e.volume === null || e.volume === '' ? null : e.volume,
            dispositivo: dispositivo,
            createdAt: formatDateToCustomString(new Date()),
          };

          toSavePriceSearch.push(obj);
        }
      });

      if (toSavePriceSearch?.length == 0) {
        setShowMessageForm({
          text: `Ocorreu um erro durante o envio da pesquisa - Não há nada a ser salvo na base de dados!`,
          icon: WarningIcon2,
          type: "error",
        });
      } else {
        setUltimosDadosColetados(dadosColetados);
        handleSavePriceSearch(toSavePriceSearch);
        setWasActionCalled(true);
      }

      setIsLoadingLocation(false);
    },
    [
      closedModalUpdate,
      getDispositivo,
      handleSavePriceSearch,
      idCliente,
      setDadosColetados,
      setShowMessageForm,
      setUltimosDadosColetados,
      setIsLoadingLocation,
      setWasActionCalled,
    ]
  );

  useEffect(() => {
    if (Object.entries(dadosColetados).length > 0) {
      processPriceSearchData(dadosColetados);
    }
  }, [dadosColetados, processPriceSearchData]);

  const [isFormDirty, setIsFormDirty] = useState(false);
  const [comparedValues, setComparedValues] = useState(null);
  const [previousUpdateStatus, setPreviousUpdateStatus] = useState(false);

  useEffect(() => {
    if (comparedValues) {
      if (comparedValues.length) {
        setSendWithoutUpdate(true);
      } else {
        openModalUpdateAction();
      }
    }
  }, [comparedValues]);

  async function updateComparedValues() {
    const mapas = [];
    await db.mapaDaMina
      .where("clienteId")
      .equals(Number.parseInt(idCliente))
      .each((e) => mapas.push(e));

    if (mapas.length > 0) {
      const updated = [];
      for (const e of mapas) {
        if (e.deletedAt || e.updated || e.new) {
          e.id = e.new ? 0 : e.id;
          updated.push(e);
        }
      }
      setComparedValues(updated);
    }
  }

  const checkforUpdates = async () => {
    const mapas = [];
    await db.mapaDaMina
      .where("clienteId")
      .equals(Number.parseInt(idCliente))
      .each((e) => mapas.push(e));

    let hasUpdates = false;
    for (const e of mapas) {
      if (e.deletedAt || e.updated || (typeof e.id === "string" && e.id.length === 15)) {
        e.id = typeof e.id === "string" && e.id.length === 15 ? 0 : e.id;
        hasUpdates = true;
      }
    }

    return hasUpdates;
  };

  useEffect(() => {
    if (deletar && !Number.isNaN(Number.parseInt(deletar))) {
      const toDeleteId = Number.parseInt(deletar);
      db.mapaDaMina
        .where("id")
        .equals(toDeleteId)
        .and((e) => !e.deletedAt && !e.deletadoEm)
        .first()
        .then((e) => {
          if (e) setDeletarId(Number.parseInt(deletar));
        });
    }
  }, [deletar]);

  const toEditModalShow = async () => {
    await updateComparedValues();
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsFormDirty(false);
    toEditModalShow();
  };

  const handleInputChange = () => {
    setIsFormDirty(true);
  };

  const [showMessageModal, setShowMessageModal] = useState({
    text: "",
    type: "success",
  });

  const {
    buttonConfigs,
    setTipoMensagem,
    checkRouteRoteirizacao,
    loadingVisita,
    loadingPost: isLoadingPostModal,
  } = useButtonsConfig({
    dados: {
      fechado: fechadoConsomeBasico && fechadoConsomeBasico == "clienteFechado",
      consomeBasico: !(fechadoConsomeBasico && fechadoConsomeBasico == "clienteNaoConsomeBasicos"),
    },
    idCliente: parseInt(idCliente),
    auto: false,
    isOnline,
    setShowMessageModal,
    open,
    origem,
    cameFromVisitaView,
  });

  const handleFechado = () => {
    setTipoMensagem("default-inativo");
    buttonConfigs.fechado.open();
  };

  const handleManterFechado = () => {
    setTipoMensagem("default-inativo");
    buttonConfigs.manterFechado.open();
  };

  const handleManterNaoConsome = () => {
    setTipoMensagem("default-inativo");
    buttonConfigs.manterNaoConsome.open();
  };

  const handleNaoConsome = () => {
    setTipoMensagem("default-inativo");
    buttonConfigs.naoConsome.open();
  };

  const { closeModalAction: closeModalResponse } = useModalAction();

  const { isModalOpenAction: isModalClearResponse, closeModalAction: closeModalclearResponse } = useModalAction();

  async function toCloseModalDelete() {
    setSelectedSearchId(0);
    closeModalDeleteAction();
  }

  const { isModalOpenAction: isModalDeleteOpenAction, closeModalAction: closeModalDeleteAction } = useModalAction();

  const [sendWithoutUpdate, setSendWithoutUpdate] = useState(false);

  useEffect(() => {
    if (sendWithoutUpdate) {
      closeModalUpdateAction();

      (async () => {
        const mapas = [];
        await db.mapaDaMina
          .where("clienteId")
          .equals(Number.parseInt(idCliente))
          .each((e) => mapas.push(e));
        setSendWithoutUpdate(false);
        verifyClientShare(mapas);
      })();
    }
  }, [sendWithoutUpdate]);

  const {
    isModalOpenAction: isModalUpdateOpenAction,
    closeModalAction: closeModalUpdateAction,
    openModalAction: openModalUpdateAction,
  } = useModalAction();

  const { isModalOpen, closeModal, modalStatus, messageStatus, submessageStatus } = useModalResponse();

  const [isOpenClientShareModal, setIsOpenClientShareModal] = useState(false);
  const [clientShareModalParams, setClientShareModalParams] = useState({});
  const [clientShareConfirmed, setClientShareConfirmed] = useState(false);

  const { verifyClientShare } = useClientShare(
    setClientShareModalParams,
    setIsOpenClientShareModal,
    setClientShareConfirmed,
    pesquisaObjetos
  );

  useUnsavedChangesWarning(isFormDirty, navigate, "Nova Pesquisa");

  useEffect(() => {
    if (clientShareConfirmed) {
      (async () => {
        const mapas = [];
        await db.mapaDaMina
          .where("clienteId")
          .equals(Number.parseInt(idCliente))
          .each((e) => mapas.push(e));
        verifyRequiredGroups(mapas);
      })();

      setClientShareConfirmed(false);
    }
  }, [clientShareConfirmed]);

  const handleClearCache = async () => {
    if (isClearing) {
      return;
    }

    setIsClearing(true);

    const result = await clearCache(idCliente);

    if (result.success) {
      const newTimestamp = Date.now();
      reset();
      updateHook();
      refetchMapaMina();
      setButtonClickClear(newTimestamp);
      setComponentKey(newTimestamp);
    } else {
      setShowMessageForm({
        text: result.message || "Erro ao limpar cache",
        type: "error",
        icon: WarningIcon2,
      });
    }

    setIsClearing(false);
  };

  const handleRefetch = async () => {
    refetchMapaMina();
  };

  useConfigureFooter(
    {
      form: !(dataIsNotPresentable || availableCardsCount == 0),
      formId: "pesquisa",
      submitLabel: "Concluir pesquisa",
      submitVariation: "green",

      handleModalClear: handleClearCache,
    },
    [dataIsNotPresentable, availableCardsCount]
  );

  const [isOpenRequiredGroupsModal, setIsOpenRequiredGroupsModal] = useState(false);
  const [requiredGroupsParams, setRequiredGroupsParams] = useState({});
  const { verifyRequiredGroups } = useRequiredGroups({
    trilha: trilhaCliente,
    setRequiredGroupsParams,
    setIsOpenRequiredGroupsModal,
    openModalUpdate,
    pesquisaObjetos,
  });

  useEffect(() => {
    if (wasActionCalled && !isLoadingPostModal) {
      handleRefetch();
      setWasActionCalled(false);
    }
  }, [isLoadingPostModal]);

  useEffect(() => {
    if (wasActionCalled && !isLoadingPost) {
      handleRefetch();
      setWasActionCalled(false);
    }
  }, [isLoadingPost]);

  return (
    <>
      {isLoadingData ||
      loading ||
      isLoadingPost ||
      isLoadingPostModal ||
      loadingMapaData ||
      isClearing ||
      isLoadingLocation ||
      loadingVisita ? (
        <LoaderFull />
      ) : (
        <>
          <ClientHeader dados={cliente} />
          <PriceSearchDisclaimer />
          <FormProvider {...methods}>
            <Form
              id="pesquisa"
              onSubmit={handleSubmit}
              onChange={handleInputChange}
              className="flex space-x-4 mt-4 container"
            >
              <div className="w-full">
                <PriceSearchFilter
                  key={`filter-${componentKey}`}
                  graphsFilter={dataLookupFilters}
                  totalItems={totalItems}
                  lastSearchDate={lastSearchDate}
                  filteredItemsCount={filteredItemsCount}
                  loading={loadingFilters}
                  origem={origem}
                  cameFromVisitaView={cameFromVisitaView}
                />

                {isLoadingData || loading || isLoadingPost || loadingMapaData ? (
                  <div className="flex flex-col items-center justify-center py-12 space-y-4">
                    <Spinner size="lg" color="blue.500" />
                    <p className="text-gray-600 text-sm">Limpando alterações...</p>
                  </div>
                ) : (
                  <>
                    <PriceSearchCharts
                      key={`charts-${componentKey}`}
                      filters={filters}
                      trilha={trilhaCliente}
                      clienteId={idCliente}
                      filteredData={filteredData}
                      buttonClickClear={buttonClickClear}
                    />
                    {loadingMapaData && (
                      <p className="flex items-center gap-2 text-sm">
                        <Spinner size={"sm"} />
                        Verificando se há pesquisas
                      </p>
                    )}

                    {searchedCount > 0 && filteredOnce > 0 && dataIsNotPresentable ? (
                      <WarningBar
                        loading={loading}
                        loadingRotaVisita={loadingVisita}
                        pesquisas={filteredData}
                        clienteFechado={fechadoConsomeBasico && fechadoConsomeBasico == "clienteFechado"}
                        manterFechado={true}
                        naoConsomeProdutosBasicos={
                          fechadoConsomeBasico && fechadoConsomeBasico == "clienteNaoConsomeBasicos"
                        }
                        handleFechado={handleFechado}
                        handleNaoConsome={handleNaoConsome}
                        handleManterFechado={handleManterFechado}
                        handleManterNaoConsome={handleManterNaoConsome}
                      />
                    ) : (
                      <PriceSearchCards
                        key={`cards-${componentKey}`}
                        pesquisaObjetos={pesquisaObjetos}
                        pesquisas={filteredData}
                        toDeleteModalShow={toggleDeleteModal}
                        dados={cliente}
                        loading={loading}
                        lastSearchDate={lastSearchDate}
                        filters={filters}
                        updateHook={updateHook}
                        diasMostrarDataAtualizacao={diasMostrarDataAtualizacao}
                        buttonClickClear={buttonClickClear}
                      />
                    )}
                  </>
                )}

                {showMessageForm.text ? (
                  <MessageForm
                    text={showMessageForm.text}
                    type={showMessageForm.type}
                    showMessage={showMessageForm}
                    setShowMessage={() =>
                      setShowMessageForm({
                        text: "",
                      })
                    }
                    icon={showMessageForm.icon}
                  />
                ) : null}

                {showMessageFormPost.text ? (
                  <MessageForm
                    text={showMessageFormPost.text}
                    type={showMessageFormPost.type}
                    showMessage={showMessageFormPost}
                    setShowMessage={() =>
                      setShowMessageFormPost({
                        text: "",
                      })
                    }
                    icon={showMessageFormPost.icon}
                  />
                ) : null}
              </div>
            </Form>
          </FormProvider>
        </>
      )}

      <ModalAction
        isOpen={isDeleteModalOpen}
        onClose={toggleDeleteModal}
        title="Deseja deletar a pesquisa selecionada?"
        sub="A ação não pode ser revertida"
        buttonOne="Sim"
        buttonTwo="Não"
        linkOne={deleteConfirmed}
        linkTwo={toggleDeleteModal}
      />

      {showMessageModal?.text && (
        <ModalResponse
          isOpen={true}
          closeModal={() => {
            const onClose = showMessageModal.onClose;
            setShowMessageModal((prev) => ({
              ...prev,
              text: "",
              onClose: () => {},
            }));

            closeModalResponse();

            if (onClose && typeof onClose == "function") onClose();
          }}
          status={showMessageModal.type ?? ""}
          message={showMessageModal.text ?? ""}
          submessage={showMessageModal.submessage ?? ""}
          setOfflineReridect={true}
          checkin={checkin}
          checkRouteRoteirizacao={checkRouteRoteirizacao}
          idCliente={idCliente}
          button="Ok"
        />
      )}

      <ModalResponse
        isOpen={isModalClearResponse}
        closeModal={closeModalclearResponse}
        status={"success"}
        message={"Cache limpo com sucesso!"}
        submessage={""}
        button="Ok"
      />

      <ModalAction
        isOpen={isModalDeleteOpenAction}
        onClose={toCloseModalDelete}
        title="Deseja deletar a pesquisa selecionada?"
        sub="A ação não pode ser revertida"
        buttonOne="Sim"
        buttonTwo="Não"
        linkOne={deleteConfirmed}
        linkTwo={toCloseModalDelete}
      />

      <ModalAction
        isOpen={isModalUpdateOpenAction}
        onClose={closeModalUpdateAction}
        title="Nenhuma informação foi modificada!"
        sub="Deseja concluir a pesquisa mesmo assim?"
        buttonOne="Sim"
        buttonTwo="Não"
        linkOne={() => {
          setSendWithoutUpdate(true);
        }}
        linkTwo={closeModalUpdateAction}
      />

      <ModalResponse
        isOpen={isModalOpen}
        closeModal={closeModal}
        status={modalStatus}
        message={messageStatus}
        submessage={submessageStatus}
        button="Ok"
      />

      <ModalForUpdate
        trilha={trilhaCliente}
        isOpen={isModalUpdate}
        onClose={() => {
          closedModalUpdate();
        }}
        title="Dados Atualizados!"
        sub=""
        buttonOne={null}
        buttonTwo="Ok"
        linkOne={null}
        linkTwo={null}
        onConfirm={(e) => onConfirmModal(e)}
        setDados={dadosColetados}
      />

      <ModalAction
        isOpen={buttonConfigs.fechado.isModal}
        onClose={() => buttonConfigs.fechado.closeModal()}
        title="Aviso!"
        sub={buttonConfigs.fechado.sub}
        buttonOne="Sim"
        buttonTwo="Não"
        linkOne={async () => {
          setWasActionCalled(true);
          await buttonConfigs.fechado.action()();
        }}
        linkTwo={() => buttonConfigs.fechado.closeModal()}
      />

      <ModalAction
        isOpen={buttonConfigs.naoConsome.isModal}
        onClose={() => buttonConfigs.naoConsome.closeModal()}
        title="Aviso!"
        sub={buttonConfigs.naoConsome.sub}
        buttonOne="Sim"
        buttonTwo="Não"
        linkOne={async () => {
          setWasActionCalled(true);
          await buttonConfigs.naoConsome.action()();
        }}
        linkTwo={() => buttonConfigs.naoConsome.closeModal()}
      />

      <ModalAction
        isOpen={buttonConfigs.manterFechado.isModal}
        onClose={() => buttonConfigs.manterFechado.closeModal()}
        title="Aviso!"
        sub={buttonConfigs.manterFechado.sub}
        buttonOne="Sim"
        buttonTwo="Não"
        linkOne={async () => {
          setWasActionCalled(true);
          await buttonConfigs.manterFechado.action()();
        }}
        linkTwo={() => buttonConfigs.manterFechado.closeModal()}
      />

      <ModalAction
        isOpen={buttonConfigs.manterNaoConsome.isModal}
        onClose={() => buttonConfigs.manterNaoConsome.closeModal()}
        title="Aviso!"
        sub={buttonConfigs.manterNaoConsome.sub}
        buttonOne="Sim"
        buttonTwo="Não"
        linkOne={async () => {
          setWasActionCalled(true);
          await buttonConfigs.manterNaoConsome.action()();
        }}
        linkTwo={() => buttonConfigs.manterNaoConsome.closeModal()}
      />

      <PriceSearchClientShareModal
        isOpen={isOpenClientShareModal}
        setIsOpen={setIsOpenClientShareModal}
        {...clientShareModalParams}
        handleConfirm={() => {
          setClientShareConfirmed(true);
        }}
      />

      <PriceSearchRequiredGroups
        isOpen={isOpenRequiredGroupsModal}
        setIsOpen={setIsOpenRequiredGroupsModal}
        {...requiredGroupsParams}
        handleConfirm={openModalUpdate}
      />

      {(!origem || origem != origens.editar_pesquisa) && !fechadoConsomeBasico && (
        <SearchNotUpdatedModal searches={filteredData} />
      )}
    </>
  );
};

export default PriceSearch;
